import { z } from 'zod';
import { idSchema } from './common.js';
import { languageIssues } from './language.js';
import { learnerChars, limitedTexts, paragraphsOf, preferredIssues, sceneIssueParts, } from './publish-rules.js';
/**
 * Fix with AI — one field of one scene, rewritten to meet the rules it breaks.
 *
 * The rules declare whether they are fixable (`SceneIssue.fixable`); this module turns
 * them into what the console shows beside a field and what the fix handler checks its
 * answer against, so the two cannot disagree. `docs/decisions/fix-with-ai.md`.
 */
/** The fields a fix may rewrite. `items` is a question's option set, rewritten whole. */
export const fixFieldSchema = z
    .enum(['prompt', 'body', 'hint', 'items', 'explanation'])
    .describe('The one field of the scene to rewrite.');
export const sceneWarningSchema = z
    .object({
    field: z
        .string()
        .describe('The scene field the rule is about, as the editor names it.'),
    message: z.string().describe('The rule, and what it received.'),
    fixable: z
        .boolean()
        .describe('Whether AI may rewrite the field to meet the rule.'),
    blocks: z
        .boolean()
        .describe('Whether it stops the lesson publishing. False for a warning, and for text that predates the rule.'),
})
    .describe('One rule a scene field breaks.');
const keyOf = (one) => `${one.path.join('.')}\n${one.message}`;
/**
 * Every rule the scene breaks, blocking or not, with the field it is about.
 *
 * Text written before the subheadline rules, or in a lesson published at some point, is
 * still measured against them: they warn rather than block, which is the state a reviewer
 * most needs to see. The language standard’s warnings ride along, so a fix knows not to
 * add to them.
 */
export function sceneWarnings(scene, 
/** The lesson's, so a figure seating someone it does not cast warns before publish. */
cast) {
    // Only exempt text makes the gate's view differ from the full one, so only then is
    // it worth running the rules twice.
    const gated = scene.bodyPredatesRules === true || scene.predatesLimits === true
        ? new Set(sceneIssueParts(scene, cast).map(keyOf))
        : undefined;
    const rules = sceneIssueParts({ ...scene, bodyPredatesRules: false, predatesLimits: false }, cast).map((one) => ({
        field: one.path[0] ?? '',
        message: one.message,
        fixable: one.fixable,
        blocks: gated?.has(keyOf(one)) ?? true,
    }));
    const texts = [
        ['prompt', scene.prompt],
        ['body', scene.body],
        ['hint', scene.hint],
        ...scene.itemLabels.map((label) => ['items', label]),
        ['explanation', scene.explanation],
    ];
    const language = texts.flatMap(([field, text]) => languageIssues(text ?? '')
        .filter((one) => !one.blocks)
        .map((one) => ({
        field,
        message: one.message,
        fixable: false,
        blocks: false,
    })));
    // Above the preferred range, over MAX too: advice that blocks nothing — GEN-232.
    const preferred = preferredIssues(limitedTexts(scene)).map((one) => ({
        field: one.path[0] ?? '',
        message: one.message,
        fixable: one.fixable,
        blocks: false,
    }));
    return [...rules, ...preferred, ...language];
}
/** The warnings on one field that a fix may act on. */
export const fixableOn = (warnings, field) => warnings.filter((one) => one.field === field && one.fixable);
/** What the "Fixed" line reports about text that now passes, counted as the limits count. */
export function textCounts(text) {
    return {
        characters: learnerChars(text),
        paragraphs: paragraphsOf(text).length,
    };
}
/** POST /authoring/scenes/:id/fixes */
export const fixSceneSchema = z
    .strictObject({
    lessonId: idSchema.describe('The lesson the scene is being read in, whose other scenes are the fix’s context.'),
    field: fixFieldSchema,
})
    .describe('Rewrite one field of a scene so it meets the rules it breaks.');
/**
 * One piece of text the fix replaced. A text field is one change keyed by its own name;
 * the option set is one per option, keyed by the item's id.
 */
export const fieldChangeSchema = z.object({
    key: z.string().describe('The field name, or the option’s item id.'),
    before: z.string().describe('The text before the fix, exactly.'),
    after: z.string().describe('The text the fix saved.'),
});
export const sceneFixSchema = z
    .object({
    field: fixFieldSchema,
    changes: z.array(fieldChangeSchema),
    cleared: z
        .array(z.string())
        .describe('The warnings on the field the fix cleared.'),
    remaining: z
        .array(sceneWarningSchema)
        .describe('The warnings still on the field after the fix.'),
})
    .describe('A fix that was saved, and how to put it back.');
//# sourceMappingURL=scene-fix.js.map